import api from './client'
import type { CreateTicketPayload, PaginatedResponse, Ticket, TicketComment, TicketFilters, TicketStatsFilters, TicketStatsResponse, TicketStatus, TicketType, TicketTypePayload } from '../api'

export const ticketApi = {
  list: async (params: TicketFilters = {}): Promise<PaginatedResponse<Ticket>> => {
    const response = await api.get<PaginatedResponse<Ticket>>('/tickets', { params })
    return response.data
  },

  get: async (ticketId: string): Promise<Ticket> => {
    const response = await api.get<Ticket>(`/tickets/${ticketId}`)
    return response.data
  },

  update: async (
    ticketId: string,
    payload: Partial<Pick<Ticket, 'subject' | 'body' | 'priority' | 'category' | 'sla_target' | 'sla_type' | 'sla_custom_days' | 'merchant_id' | 'lead_id'>>,
  ): Promise<Ticket> => {
    const response = await api.patch<Ticket>(`/tickets/${ticketId}`, payload)
    return response.data
  },

  assign: async (ticketId: string, assignedTo: string): Promise<Ticket> => {
    const response = await api.post<Ticket>(`/tickets/${ticketId}/assign`, { assigned_to: assignedTo })
    return response.data
  },

  addAssignee: async (ticketId: string, userId: string): Promise<Ticket> => {
    const response = await api.post<Ticket>(`/tickets/${ticketId}/assignees`, { user_id: userId })
    return response.data
  },

  removeAssignee: async (ticketId: string, userId: string): Promise<Ticket> => {
    const response = await api.delete<Ticket>(`/tickets/${ticketId}/assignees/${userId}`)
    return response.data
  },

  listComments: async (ticketId: string): Promise<TicketComment[]> => {
    const response = await api.get<{ data: TicketComment[] }>(`/tickets/${ticketId}/comments`)
    return response.data.data
  },

  postComment: async (
    ticketId: string,
    payload: { body: string; notify_merchant?: boolean; attachments?: File[] },
  ): Promise<TicketComment> => {
    const formData = new FormData()
    formData.append('body', payload.body)
    if (payload.notify_merchant) formData.append('notify_merchant', '1')
    ;(payload.attachments ?? []).forEach((file) => formData.append('attachments[]', file))
    const response = await api.post<TicketComment>(`/tickets/${ticketId}/comments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  draftHelpArticle: async (ticketId: string): Promise<{ message: string; article: { id: string; slug: string; status: string; title: string } }> => {
    const response = await api.post<{ message: string; article: { id: string; slug: string; status: string; title: string } }>(`/tickets/${ticketId}/help-article-draft`)
    return response.data
  },

  updateStatus: async (
    ticketId: string,
    payload: { status: TicketStatus; resolution_notes?: string },
  ): Promise<Ticket> => {
    const response = await api.post<Ticket>(`/tickets/${ticketId}/status`, payload)
    return response.data
  },

  create: async (payload: CreateTicketPayload): Promise<Ticket> => {
    const response = await api.post<Ticket>('/tickets', payload)
    return response.data
  },

  completeChecklistItem: async (
    ticketId: string,
    itemId: string,
    payload: { completion_comment?: string | null; completion_file?: File | null },
  ): Promise<Ticket> => {
    const formData = new FormData()
    if (payload.completion_comment) formData.append('completion_comment', payload.completion_comment)
    if (payload.completion_file) formData.append('completion_file', payload.completion_file)
    const response = await api.post<Ticket>(`/tickets/${ticketId}/checklist/${itemId}/complete`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  uncompleteChecklistItem: async (ticketId: string, itemId: string): Promise<Ticket> => {
    const response = await api.post<Ticket>(`/tickets/${ticketId}/checklist/${itemId}/uncomplete`)
    return response.data
  },

  linkArticle: async (
    ticketId: string,
    payload: { article_id: string; solved: boolean },
  ): Promise<Ticket> => {
    const response = await api.post<Ticket>(`/tickets/${ticketId}/articles`, payload)
    return response.data
  },

  updateArticleLink: async (
    ticketId: string,
    articleId: string,
    payload: { solved: boolean },
  ): Promise<Ticket> => {
    const response = await api.patch<Ticket>(`/tickets/${ticketId}/articles/${articleId}`, payload)
    return response.data
  },

  unlinkArticle: async (ticketId: string, articleId: string): Promise<Ticket> => {
    const response = await api.delete<Ticket>(`/tickets/${ticketId}/articles/${articleId}`)
    return response.data
  },

  listForMerchant: async (merchantId: string): Promise<PaginatedResponse<Ticket>> => {
    const response = await api.get<PaginatedResponse<Ticket>>(`/merchants/${merchantId}/tickets`, {
      params: { per_page: 10 },
    })
    return response.data
  },

  stats: async (params: TicketStatsFilters = {}): Promise<TicketStatsResponse> => {
    const response = await api.get<TicketStatsResponse>('/tickets/stats', { params })
    return response.data
  },

  updateStaleDays: async (staleAfterDays: number): Promise<{ stale_after_days: number }> => {
    const response = await api.patch<{ stale_after_days: number }>('/tickets/stats/settings', {
      stale_after_days: staleAfterDays,
    })
    return response.data
  },

  listForLead: async (leadId: string): Promise<PaginatedResponse<Ticket>> => {
    const response = await api.get<PaginatedResponse<Ticket>>(`/leads/${leadId}/tickets`, {
      params: { per_page: 10 },
    })
    return response.data
  },
}

export const ticketTypeApi = {
  list: async (params: { include_archived?: boolean } = {}): Promise<TicketType[]> => {
    const response = await api.get<TicketType[]>('/settings/ticket-types', { params })
    return response.data
  },

  get: async (ticketTypeId: string): Promise<TicketType> => {
    const response = await api.get<TicketType>(`/settings/ticket-types/${ticketTypeId}`)
    return response.data
  },

  create: async (payload: TicketTypePayload): Promise<TicketType> => {
    const response = await api.post<TicketType>('/settings/ticket-types', payload)
    return response.data
  },

  update: async (ticketTypeId: string, payload: Partial<TicketTypePayload>): Promise<TicketType> => {
    const response = await api.patch<TicketType>(`/settings/ticket-types/${ticketTypeId}`, payload)
    return response.data
  },

  archive: async (ticketTypeId: string): Promise<TicketType> => {
    const response = await api.post<TicketType>(`/settings/ticket-types/${ticketTypeId}/archive`)
    return response.data
  },

  restore: async (ticketTypeId: string): Promise<TicketType> => {
    const response = await api.post<TicketType>(`/settings/ticket-types/${ticketTypeId}/restore`)
    return response.data
  },

  reorder: async (ticketTypeId: string, sortOrder: number): Promise<TicketType> => {
    const response = await api.post<TicketType>(`/settings/ticket-types/${ticketTypeId}/reorder`, { sort_order: sortOrder })
    return response.data
  },
}
